import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { isNativeMobile } from '../mobile/platform.js';
import type { PeerCallSignal, PeerDeviceKey } from '../mobile/secureChannel.js';

export type PeerCallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'failed';

interface PeerVoiceOffer {
  protocol: 'veritas-p2p-voice-v1';
  callId: string;
  type: 'offer';
  sdp: RTCSessionDescriptionInit;
  expiresAt: string;
  media: {
    profile: 'AES-256-GCM-ENCODED-AUDIO';
    callerToRecipientKey: string;
    recipientToCallerKey: string;
  };
}

interface PeerVoiceAnswer {
  protocol: 'veritas-p2p-voice-v1';
  callId: string;
  type: 'answer';
  sdp: RTCSessionDescriptionInit;
}

interface PeerVoiceControl {
  protocol: 'veritas-p2p-voice-v1';
  callId: string;
  type: 'decline' | 'hangup';
  at: string;
}

type PeerVoicePayload = PeerVoiceOffer | PeerVoiceAnswer | PeerVoiceControl;

interface ActivePeerCall {
  callId: string;
  role: 'caller' | 'recipient';
  peer: RTCPeerConnection;
  worker: Worker;
  localStream: MediaStream;
  recipientDevices: PeerDeviceKey[];
  remoteDevice: PeerDeviceKey | null;
}

type IncomingPeerCall = PeerCallSignal<PeerVoiceOffer>;

interface RingbackHandle {
  context: AudioContext;
  gain: GainNode;
  oscillators: OscillatorNode[];
  cycle: number;
}

function encodedAudioSupported(): boolean {
  const root = globalThis as typeof globalThis & { RTCRtpScriptTransform?: unknown };
  return typeof root.RTCRtpScriptTransform === 'function'
    && typeof globalThis.RTCRtpSender !== 'undefined'
    && 'transform' in globalThis.RTCRtpSender.prototype
    && typeof globalThis.RTCRtpReceiver !== 'undefined'
    && 'transform' in globalThis.RTCRtpReceiver.prototype;
}

function bytesToBase64(value: Uint8Array): string {
  let binary = '';
  for (const part of value) binary += String.fromCharCode(part);
  return btoa(binary);
}

function validMediaKey(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try { return atob(value).length === 32; } catch { return false; }
}

function validSessionDescription(
  value: unknown, expectedType: 'offer' | 'answer',
): value is RTCSessionDescriptionInit {
  if (!value || typeof value !== 'object') return false;
  const description = value as RTCSessionDescriptionInit;
  return description.type === expectedType && typeof description.sdp === 'string'
    && description.sdp.length > 0 && description.sdp.length <= 200_000;
}

function validOffer(signal: PeerCallSignal<unknown>): signal is IncomingPeerCall {
  const payload = signal.payload as Partial<PeerVoiceOffer> | null;
  return signal.kind === 'offer' && Boolean(payload)
    && payload!.protocol === 'veritas-p2p-voice-v1'
    && payload!.type === 'offer' && payload!.callId === signal.callId
    && validSessionDescription(payload!.sdp, 'offer')
    && Date.parse(String(payload!.expiresAt ?? '')) > Date.now()
    && payload!.media?.profile === 'AES-256-GCM-ENCODED-AUDIO'
    && validMediaKey(payload!.media?.callerToRecipientKey)
    && validMediaKey(payload!.media?.recipientToCallerKey);
}

function validAnswer(signal: PeerCallSignal<unknown>): signal is PeerCallSignal<PeerVoiceAnswer> {
  const payload = signal.payload as Partial<PeerVoiceAnswer> | null;
  return signal.kind === 'answer' && Boolean(payload)
    && payload!.protocol === 'veritas-p2p-voice-v1'
    && payload!.type === 'answer' && payload!.callId === signal.callId
    && validSessionDescription(payload!.sdp, 'answer');
}

function waitForIceGathering(peer: RTCPeerConnection): Promise<void> {
  if (peer.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      peer.removeEventListener('icegatheringstatechange', changed);
      reject(new Error('The protected peer route could not finish network discovery.'));
    }, 12_000);
    const changed = () => {
      if (peer.iceGatheringState !== 'complete') return;
      window.clearTimeout(timeout);
      peer.removeEventListener('icegatheringstatechange', changed);
      resolve();
    };
    peer.addEventListener('icegatheringstatechange', changed);
  });
}

function attachFrameProtection(
  endpoint: RTCRtpSender | RTCRtpReceiver,
  worker: Worker,
  input: { mode: 'encrypt' | 'decrypt'; keyBase64: string; callId: string; direction: string },
): void {
  const Transform = (globalThis as typeof globalThis & {
    RTCRtpScriptTransform: new (worker: Worker, options: Record<string, string>) => unknown;
  }).RTCRtpScriptTransform;
  (endpoint as RTCRtpSender & { transform: unknown }).transform = new Transform(worker, input);
}

function createPeerConnection(
  iceServers: RTCIceServer[], callId: string, worker: Worker,
  receiveKey: string, receiveDirection: string,
  remoteAudioRef: RefObject<HTMLAudioElement | null>,
  outputDeviceId: string,
  onConnected: () => void,
  onFailure: (message: string) => void,
): RTCPeerConnection {
  const peer = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' });
  peer.ontrack = (event) => {
    attachFrameProtection(event.receiver, worker, {
      mode: 'decrypt', keyBase64: receiveKey, callId, direction: receiveDirection,
    });
    const stream = event.streams[0] ?? new MediaStream([event.track]);
    const audio = remoteAudioRef.current;
    if (!audio) return;
    audio.srcObject = stream;
    audio.autoplay = true;
    audio.muted = false;
    audio.volume = 1;
    const sink = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    const selectOutput = sink.setSinkId
      ? sink.setSinkId(outputDeviceId || 'default') : Promise.resolve();
    void selectOutput.then(() => audio.play())
      .catch(() => onFailure('Tap the call screen to enable the selected speaker.'));
  };
  peer.onconnectionstatechange = () => {
    if (peer.connectionState === 'connected') {
      onConnected();
    } else if (['failed', 'closed'].includes(peer.connectionState)) {
      onFailure('The protected peer route was interrupted.');
    }
  };
  return peer;
}

export function usePeerCall(
  loadIceServers: () => Promise<RTCIceServer[]>,
  inputDeviceId = '',
  outputDeviceId = '',
) {
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const active = useRef<ActivePeerCall | null>(null);
  const incoming = useRef<IncomingPeerCall | null>(null);
  const handledSignals = useRef(new Set<string>());
  const ringback = useRef<RingbackHandle | null>(null);
  const setupDeadline = useRef<number | null>(null);
  const loadIceServersRef = useRef(loadIceServers);
  const [state, setState] = useState<PeerCallState>('idle');
  const [remoteVeritasId, setRemoteVeritasId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { loadIceServersRef.current = loadIceServers; }, [loadIceServers]);

  const stopRingback = useCallback(() => {
    const current = ringback.current;
    ringback.current = null;
    if (!current) return;
    window.clearInterval(current.cycle);
    current.oscillators.forEach((oscillator) => {
      try { oscillator.stop(); } catch { /* already stopped */ }
    });
    void current.context.close().catch(() => undefined);
  }, []);

  const startRingback = useCallback(() => {
    stopRingback();
    const Context = window.AudioContext;
    if (!Context) return;
    const context = new Context();
    const gain = context.createGain();
    gain.gain.value = 0;
    gain.connect(context.destination);
    const oscillators = [440, 480].map((frequency) => {
      const oscillator = context.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      oscillator.start();
      return oscillator;
    });
    const pulse = () => {
      const now = context.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(0.035, now);
      gain.gain.setValueAtTime(0, now + 2);
    };
    pulse();
    const cycle = window.setInterval(pulse, 6_000);
    ringback.current = { context, gain, oscillators, cycle };
    void context.resume().catch(() => undefined);
  }, [stopRingback]);

  const release = useCallback(() => {
    stopRingback();
    if (setupDeadline.current !== null) {
      window.clearTimeout(setupDeadline.current);
      setupDeadline.current = null;
    }
    const call = active.current;
    active.current = null;
    if (call) {
      call.peer.ontrack = null;
      call.peer.onconnectionstatechange = null;
      call.peer.close();
      call.localStream.getTracks().forEach((track) => track.stop());
      call.worker.terminate();
    }
    const audio = remoteAudioRef.current;
    if (audio) { audio.pause(); audio.srcObject = null; }
    incoming.current = null;
  }, [stopRingback]);

  const failClosed = useCallback((message: string) => {
    release();
    setState('failed');
    setError(message);
  }, [release]);

  const workerForCall = useCallback((callId: string): Worker => {
    const worker = new Worker('/peer-media-worker.js');
    worker.onmessage = (event) => {
      if (event.data?.callId !== callId) return;
      failClosed('PQE2EE media verification failed. The call was ended.');
    };
    worker.onerror = () => failClosed('PQE2EE media protection stopped. The call was ended.');
    return worker;
  }, [failClosed]);

  const startCall = useCallback(async (veritasId: string) => {
    if (!isNativeMobile()) throw new Error('VERITAS peer calls require an enrolled mobile device.');
    if (!encodedAudioSupported()) {
      throw new Error('This Android WebView cannot enforce AES-256 peer media protection. Update the device first.');
    }
    release();
    setError(null);
    setState('calling');
    setRemoteVeritasId(veritasId);
    startRingback();
    try {
      const channel = await import('../mobile/secureChannel.js');
      const recipient = await channel.resolvePeerCallRecipient(veritasId);
      const callId = crypto.randomUUID();
      const callerToRecipientKey = bytesToBase64(crypto.getRandomValues(new Uint8Array(32)));
      const recipientToCallerKey = bytesToBase64(crypto.getRandomValues(new Uint8Array(32)));
      const worker = workerForCall(callId);
      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: inputDeviceId ? { deviceId: { exact: inputDeviceId } } : true,
        video: false,
      });
      const peer = createPeerConnection(
        await loadIceServersRef.current(), callId, worker,
        recipientToCallerKey, 'recipient-to-caller', remoteAudioRef,
        outputDeviceId,
        () => {
          stopRingback();
          if (setupDeadline.current !== null) {
            window.clearTimeout(setupDeadline.current);
            setupDeadline.current = null;
          }
          setState('connected');
        },
        failClosed,
      );
      for (const track of localStream.getAudioTracks()) {
        const sender = peer.addTrack(track, localStream);
        attachFrameProtection(sender, worker, {
          mode: 'encrypt', keyBase64: callerToRecipientKey,
          callId, direction: 'caller-to-recipient',
        });
      }
      active.current = {
        callId, role: 'caller', peer, worker, localStream,
        recipientDevices: recipient.devices, remoteDevice: null,
      };
      await peer.setLocalDescription(await peer.createOffer({ offerToReceiveAudio: true }));
      await waitForIceGathering(peer);
      const offer: PeerVoiceOffer = {
        protocol: 'veritas-p2p-voice-v1', callId, type: 'offer',
        sdp: peer.localDescription!,
        expiresAt: new Date(Date.now() + 2 * 60_000).toISOString(),
        media: {
          profile: 'AES-256-GCM-ENCODED-AUDIO',
          callerToRecipientKey, recipientToCallerKey,
        },
      };
      await channel.startPeerCall(callId, recipient.recipientVeritasId, recipient.devices, offer);
      setupDeadline.current = window.setTimeout(() => {
        if (active.current?.callId === callId) {
          failClosed('The VERITAS contact did not answer. The protected offer expired.');
        }
      }, 2 * 60_000);
    } catch (cause) {
      failClosed(cause instanceof Error ? cause.message : 'The VERITAS peer call could not start.');
      throw cause;
    }
  }, [failClosed, inputDeviceId, outputDeviceId, release, startRingback, workerForCall]);

  const answer = useCallback(async () => {
    const offered = incoming.current;
    if (!offered || !validOffer(offered)) return;
    stopRingback();
    if (setupDeadline.current !== null) {
      window.clearTimeout(setupDeadline.current);
      setupDeadline.current = null;
    }
    setError(null);
    try {
      const worker = workerForCall(offered.callId);
      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: inputDeviceId ? { deviceId: { exact: inputDeviceId } } : true,
        video: false,
      });
      const peer = createPeerConnection(
        await loadIceServersRef.current(), offered.callId, worker,
        offered.payload.media.callerToRecipientKey, 'caller-to-recipient',
        remoteAudioRef,
        outputDeviceId,
        () => setState('connected'),
        failClosed,
      );
      await peer.setRemoteDescription(offered.payload.sdp);
      for (const track of localStream.getAudioTracks()) {
        const sender = peer.addTrack(track, localStream);
        attachFrameProtection(sender, worker, {
          mode: 'encrypt', keyBase64: offered.payload.media.recipientToCallerKey,
          callId: offered.callId, direction: 'recipient-to-caller',
        });
      }
      active.current = {
        callId: offered.callId, role: 'recipient', peer, worker, localStream,
        recipientDevices: [], remoteDevice: offered.sender,
      };
      await peer.setLocalDescription(await peer.createAnswer());
      await waitForIceGathering(peer);
      const response: PeerVoiceAnswer = {
        protocol: 'veritas-p2p-voice-v1', callId: offered.callId,
        type: 'answer', sdp: peer.localDescription!,
      };
      const channel = await import('../mobile/secureChannel.js');
      await channel.sendPeerCallSignal(offered.callId, 'answer', offered.sender, response);
      incoming.current = null;
      setState('calling');
    } catch (cause) {
      failClosed(cause instanceof Error ? cause.message : 'The protected call could not be answered.');
    }
  }, [failClosed, inputDeviceId, outputDeviceId, stopRingback, workerForCall]);

  const decline = useCallback(async () => {
    const offered = incoming.current;
    if (!offered) return;
    const control: PeerVoiceControl = {
      protocol: 'veritas-p2p-voice-v1', callId: offered.callId,
      type: 'decline', at: new Date().toISOString(),
    };
    try {
      const channel = await import('../mobile/secureChannel.js');
      await channel.sendPeerCallSignal(offered.callId, 'decline', offered.sender, control);
    } finally {
      release(); setState('idle'); setRemoteVeritasId(null); setError(null);
    }
  }, [release]);

  const hangup = useCallback(async () => {
    const call = active.current;
    if (!call) { release(); setState('idle'); return; }
    const control: PeerVoiceControl = {
      protocol: 'veritas-p2p-voice-v1', callId: call.callId,
      type: 'hangup', at: new Date().toISOString(),
    };
    try {
      const channel = await import('../mobile/secureChannel.js');
      const recipients = call.remoteDevice ? [call.remoteDevice] : call.recipientDevices;
      await Promise.all(recipients.map((recipient) =>
        channel.sendPeerCallSignal(call.callId, 'hangup', recipient, control)));
    } finally {
      release(); setState('idle'); setRemoteVeritasId(null); setError(null);
    }
  }, [release]);

  useEffect(() => {
    if (!isNativeMobile()) return undefined;
    let stopped = false;
    let polling = false;
    const poll = async () => {
      if (polling || stopped) return;
      polling = true;
      try {
        const channel = await import('../mobile/secureChannel.js');
        const signals = await channel.receivePeerCallSignals<PeerVoicePayload>();
        for (const signal of signals) {
          if (handledSignals.current.has(signal.signalId)) continue;
          handledSignals.current.add(signal.signalId);
          if (handledSignals.current.size > 500) handledSignals.current.clear();
          if (validOffer(signal)) {
            if (state !== 'idle' && state !== 'failed') continue;
            incoming.current = signal;
            setRemoteVeritasId(signal.sender.veritasId);
            setError(null);
            setState('ringing');
            startRingback();
            const remaining = Math.max(0, Date.parse(signal.expiresAt) - Date.now());
            setupDeadline.current = window.setTimeout(() => {
              if (incoming.current?.signalId === signal.signalId) {
                failClosed('The protected incoming call expired.');
              }
            }, remaining);
            continue;
          }
          const call = active.current;
          if (!call || call.callId !== signal.callId) continue;
          if (validAnswer(signal) && call.role === 'caller' && state === 'calling') {
            call.remoteDevice = signal.sender;
            await call.peer.setRemoteDescription(signal.payload.sdp);
            stopRingback();
          } else if (signal.kind === 'decline') {
            failClosed('The VERITAS contact declined the call.');
          } else if (signal.kind === 'hangup') {
            release(); setState('idle'); setRemoteVeritasId(null); setError(null);
          }
        }
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : '';
        if (!['mobile_activation_required', 'mobile_session_required'].includes(message)) {
          setError(message || 'Protected call signaling is temporarily unavailable.');
        }
      } finally { polling = false; }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 1_000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [failClosed, release, startRingback, state, stopRingback]);

  useEffect(() => () => release(), [release]);

  return {
    state, remoteVeritasId, error, remoteAudioRef,
    supported: isNativeMobile() && encodedAudioSupported(),
    startCall, answer, decline, hangup,
  };
}
